import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistorique } from '../lib/historique'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { useAuth, estAdmin, peutEcrire } from '../lib/auth'
import { poids } from '../lib/format'
import { messageClair } from '../lib/televersement'
import { ouvrirPage } from '../lib/decoupage'
import { ouvrirPlan } from '../lib/plans'
import {
  basculerFavori,
  chargerDocumentsDevis,
  chargerFavoris,
  chargerSectionsPages,
  chercher,
  corrigerSection,
  formaterCode,
  importerDocumentDevis,
  preparerDocumentDevis,
  regrouper,
  type DocumentDevis,
  type Etape,
  type Favori,
  type InfosImport,
  type PageSectionDevis,
  type PlageSection,
  type ResultatDevis
} from '../lib/devis'
import type { Chantier, Feuillet } from '../lib/types'
import { VisualiseurPdf, type SourcePages } from './VisualiseurPdf'
import '../devis.css'
import { JOURS_CORBEILLE, mettreALaCorbeille } from '../lib/suppression'
import { Corbeille } from '../components/Corbeille'
import { Superpose } from '../components/Superpose'

/**
 * Onglet Devis : recherche plein texte dans le devis et les addenda,
 * sections suivies, liste des sections, addenda classés par numéro.
 * L'import est réservé aux administrateurs (au bureau, sur ordinateur).
 */

interface Ouverture {
  doc: DocumentDevis
  page: number
}

/** Une section du devis telle qu'affichée : un code dans un fichier. */
interface LigneSection {
  cle: string
  document_id: string
  code: string | null
  titre: string | null
  plages: PlageSection[]
  debut: number
  nbPages: number
}

interface EtatTravail {
  fichier: number
  nbFichiers: number
  nom: string
  etape: Etape
  fraction: number
}

const LIBELLE_ETAPE: Record<Etape, string> = {
  envoi: 'Envoi du fichier',
  texte: 'Lecture du texte',
  pages: 'Préparation pour les téléphones'
}

function nomDocument(d: DocumentDevis): string {
  if (d.type === 'addenda') return `Addenda ${d.addenda_numero ?? '?'} — ${d.nom}`
  return d.nom
}

function dateCourte(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso)
  return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })
}

function pret(d: DocumentDevis): boolean {
  return Boolean(d.texte_extrait_le && d.pages_decoupees)
}

/** Tri naturel des numéros d'addenda (2 avant 10). */
function ordreAddenda(a: DocumentDevis, b: DocumentDevis): number {
  return (a.addenda_numero ?? '').localeCompare(b.addenda_numero ?? '', 'fr', { numeric: true }) || a.nom.localeCompare(b.nom)
}

export function Devis({ chantier }: { chantier: Chantier }) {
  const { profil } = useAuth()
  const admin = estAdmin(profil)
  const ecrivable = peutEcrire(profil, chantier.statut)

  const [docs, setDocs] = useState<DocumentDevis[] | null>(null)
  const [pages, setPages] = useState<PageSectionDevis[]>([])
  const [favoris, setFavoris] = useState<Favori[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [avis, setAvis] = useState<string | null>(null)
  const [ouvert, setOuvert] = useState<Ouverture | null>(null)
  useHistorique(ouvert, setOuvert, (o) => (o ? `${o.doc.id}:${o.page}` : null))

  const [requete, setRequete] = useState('')
  const [recherche, setRecherche] = useState<{ requete: string; termes: string[]; resultats: ResultatDevis[]; plus: boolean } | null>(null)
  const [enRecherche, setEnRecherche] = useState(false)

  const [edition, setEdition] = useState(false)
  const [travail, setTravail] = useState<EtatTravail | null>(null)
  const [formAddenda, setFormAddenda] = useState(false)
  const [versionCorbeille, setVersionCorbeille] = useState(0)
  const arret = useRef(false)

  const charger = useCallback(async () => {
    try {
      const [d, p, f] = await Promise.all([
        chargerDocumentsDevis(chantier.id),
        chargerSectionsPages(chantier.id),
        chargerFavoris(chantier.id)
      ])
      setDocs(d)
      setPages(p)
      setFavoris(f)
    } catch (e) {
      setErreur(
        navigator.onLine
          ? messageClair(e)
          : "Pas de réseau : le devis s'affiche seulement en ligne. Les pages déjà ouvertes restent dans l'onglet de chaque page."
      )
      setDocs((x) => x ?? [])
    }
  }, [chantier.id])

  useEffect(() => {
    arret.current = false
    void charger()
    return () => {
      arret.current = true
    }
  }, [charger])

  const parId = useMemo(() => new Map((docs ?? []).map((d) => [d.id, d])), [docs])
  const devisDocs = useMemo(() => (docs ?? []).filter((d) => d.type === 'devis'), [docs])
  const addenda = useMemo(() => (docs ?? []).filter((d) => d.type === 'addenda').sort(ordreAddenda), [docs])
  const plages = useMemo(() => regrouper(pages), [pages])
  const suivis = useMemo(() => new Set(favoris.map((f) => f.code)), [favoris])

  /** Sections du devis (pas des addenda), un rang par code et par fichier. */
  const sections = useMemo<LigneSection[]>(() => {
    const ids = new Set(devisDocs.map((d) => d.id))
    const parCle = new Map<string, LigneSection>()
    for (const p of plages) {
      if (!ids.has(p.document_id)) continue
      const cle = `${p.document_id}|${p.code ?? ''}`
      const l = parCle.get(cle)
      if (l) {
        l.plages.push(p)
        l.nbPages += p.fin - p.debut + 1
        l.debut = Math.min(l.debut, p.debut)
        if (!l.titre && p.titre) l.titre = p.titre
      } else {
        parCle.set(cle, {
          cle,
          document_id: p.document_id,
          code: p.code,
          titre: p.titre,
          plages: [p],
          debut: p.debut,
          nbPages: p.fin - p.debut + 1
        })
      }
    }
    return [...parCle.values()].sort((a, b) => {
      if (!a.code !== !b.code) return a.code ? -1 : 1
      return (
        (a.code ?? '').localeCompare(b.code ?? '') ||
        (parId.get(a.document_id)?.nom ?? '').localeCompare(parId.get(b.document_id)?.nom ?? '') ||
        a.debut - b.debut
      )
    })
  }, [plages, devisDocs, parId])

  const plusieursFichiers = devisDocs.length > 1

  async function lancerRecherche() {
    const q = requete.trim()
    if (!q) {
      setRecherche(null)
      return
    }
    setEnRecherche(true)
    setErreur(null)
    try {
      const r = await chercher(chantier.id, q)
      if (r.termes.length === 0) {
        setErreur('Tape au moins un mot de deux lettres.')
        setRecherche(null)
      } else setRecherche({ requete: q, termes: r.termes, resultats: r.resultats, plus: r.plus })
    } catch (e) {
      setErreur(messageClair(e))
    } finally {
      setEnRecherche(false)
    }
  }

  async function suivre(code: string, titre: string | null, oui: boolean) {
    const avant = favoris
    setFavoris(oui ? [...favoris, { code, titre }].sort((a, b) => a.code.localeCompare(b.code)) : favoris.filter((f) => f.code !== code))
    try {
      await basculerFavori(chantier.id, { code, titre }, oui)
    } catch (e) {
      setFavoris(avant)
      setErreur("La section suivie n'a pas été enregistrée : " + messageClair(e))
    }
  }

  async function importer(fichiers: File[], infos: InfosImport) {
    if (fichiers.length === 0) return
    arret.current = false
    setErreur(null)
    setAvis(null)
    setFormAddenda(false)
    const sansTexte: string[] = []
    try {
      for (let i = 0; i < fichiers.length; i++) {
        const f = fichiers[i]
        setTravail({ fichier: i + 1, nbFichiers: fichiers.length, nom: f.name, etape: 'envoi', fraction: 0 })
        const r = await importerDocumentDevis(
          chantier.id,
          f,
          infos,
          profil?.id ?? null,
          (etape, fraction) => setTravail({ fichier: i + 1, nbFichiers: fichiers.length, nom: f.name, etape, fraction }),
          () => arret.current
        )
        if (r.sansTexte) sansTexte.push(f.name)
      }
      if (sansTexte.length) {
        setAvis(
          `Presque aucun texte n'a été trouvé dans : ${sansTexte.join(', ')}. C'est probablement un devis numérisé ` +
            '(une image) : il se consulte, mais la recherche ne trouvera rien dedans. Demande une version PDF « texte » à l’architecte.'
        )
      }
    } catch (e) {
      setErreur(
        "L'import s'est arrêté : " + messageClair(e) + ' — le fichier reste dans la liste « Fichiers » ; « Reprendre » termine la préparation.'
      )
    } finally {
      setTravail(null)
      await charger()
    }
  }

  async function reprendre(d: DocumentDevis) {
    arret.current = false
    setErreur(null)
    try {
      setTravail({ fichier: 1, nbFichiers: 1, nom: d.nom, etape: 'envoi', fraction: 0 })
      await preparerDocumentDevis(
        d,
        (etape, fraction) => setTravail({ fichier: 1, nbFichiers: 1, nom: d.nom, etape, fraction }),
        () => arret.current
      )
    } catch (e) {
      setErreur('La préparation a échoué : ' + messageClair(e))
    } finally {
      setTravail(null)
      await charger()
    }
  }

  async function supprimer(d: DocumentDevis) {
    if (
      !window.confirm(
        `Mettre « ${nomDocument(d)} » à la corbeille ?\n\nIl disparaîtra pour toute l'équipe, recherche comprise. Vous pourrez le restaurer pendant ${JOURS_CORBEILLE} jours.`
      )
    )
      return
    try {
      await mettreALaCorbeille(d, profil?.id ?? null)
      setVersionCorbeille((v) => v + 1)
    } catch (e) {
      setErreur('Le retrait a échoué : ' + messageClair(e))
    }
    await charger()
  }

  async function corriger(plagesVisees: PlageSection[], code: string | null, titre: string | null) {
    try {
      for (const p of plagesVisees) await corrigerSection(p.document_id, p.debut, p.fin, code, titre)
    } catch (e) {
      setErreur("La correction n'a pas été enregistrée : " + messageClair(e))
    }
    setPages(await chargerSectionsPages(chantier.id).catch(() => pages))
  }

  const ouvrir = (document_id: string, page: number) => {
    const doc = parId.get(document_id)
    if (doc) setOuvert({ doc, page })
  }

  if (ouvert) {
    return (
      <LecteurDevis
        doc={ouvert.doc}
        pageInitiale={ouvert.page}
        pages={pages}
        utilisateurId={profil?.id ?? null}
        surligner={recherche?.termes}
        fermer={() => setOuvert(null)}
      />
    )
  }

  // --- Affichage -----------------------------------------------------

  const ligneSection = (l: LigneSection, avecEtoile: boolean) => {
    const doc = parId.get(l.document_id)
    const fin = l.plages.length === 1 ? l.plages[0].fin : null
    return (
      <div key={l.cle} className="carte bande-nulle devis-rang" style={{ padding: 0 }}>
        <button className="devis-ligne" onClick={() => ouvrir(l.document_id, l.debut)}>
          <span className="devis-code" style={{ color: l.code ? 'var(--ink)' : 'var(--ink3)' }}>{l.code ?? '—'}</span>
          <span className="devis-titre">
            {l.code ? l.titre ?? <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>Sans titre</span> : 'Pages sans section'}
            {(plusieursFichiers || !l.code) && doc && <small>{doc.nom}</small>}
          </span>
          <span className="code">
            {fin && fin !== l.debut ? `p. ${l.debut}–${fin}` : `p. ${l.debut}`}
            {l.plages.length > 1 ? ` · ${l.nbPages} p.` : ''}
          </span>
        </button>
        {avecEtoile && l.code && (
          <button
            className={`devis-etoile${suivis.has(l.code) ? ' oui' : ''}`}
            disabled={!ecrivable}
            title={suivis.has(l.code) ? 'Ne plus suivre cette section' : 'Suivre cette section (pour toute l’équipe)'}
            aria-label={suivis.has(l.code) ? 'Ne plus suivre' : 'Suivre'}
            onClick={() => void suivre(l.code!, l.titre, !suivis.has(l.code!))}
          >
            {suivis.has(l.code) ? '★' : '☆'}
          </button>
        )}
      </div>
    )
  }

  const rienImporte = docs !== null && docs.length === 0

  return (
    <>
      {!rienImporte && (
        <form
          className="devis-recherche"
          onSubmit={(e) => {
            e.preventDefault()
            void lancerRecherche()
          }}
        >
          <input
            type="search"
            placeholder="Chercher : pare-vapeur, uréthane, 07 21 00…"
            value={requete}
            onChange={(e) => {
              setRequete(e.target.value)
              if (!e.target.value) setRecherche(null)
            }}
            enterKeyHint="search"
          />
          <button className="bouton" type="submit" disabled={enRecherche}>
            {enRecherche ? '…' : 'Chercher'}
          </button>
        </form>
      )}

      {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}
      {avis && <p className="avis" style={{ marginBottom: 12 }}>{avis}</p>}

      {travail && (
        <div className="avis" style={{ marginBottom: 12 }}>
          <strong>
            {travail.nbFichiers > 1 ? `Fichier ${travail.fichier} sur ${travail.nbFichiers} · ` : ''}
            {LIBELLE_ETAPE[travail.etape]}… {Math.round(travail.fraction * 100)} %
          </strong>
          <br />
          {travail.nom}. Reste sur cet onglet et garde la fenêtre ouverte jusqu'à la fin.{' '}
          <button className="retour" style={{ textDecoration: 'underline' }} onClick={() => (arret.current = true)}>
            Arrêter
          </button>
        </div>
      )}

      {docs === null && <p className="vide">Chargement…</p>}

      {rienImporte && (
        <p className="vide">
          Aucun devis importé.
          <br />
          {admin
            ? 'Importe le devis depuis un ordinateur : un seul PDF ou un PDF par section, les deux fonctionnent.'
            : 'Un administrateur doit importer le devis du chantier.'}
        </p>
      )}

      {recherche ? (
        <Resultats
          recherche={recherche}
          parId={parId}
          ouvrir={ouvrir}
          effacer={() => {
            setRecherche(null)
            setRequete('')
          }}
        />
      ) : (
        docs !== null &&
        !rienImporte && (
          <>
            {favoris.length > 0 && (
              <>
                <div className="sect">
                  Sections suivies <span className="code">{favoris.length}</span>
                </div>
                <div className="liste" style={{ gap: 4 }}>
                  {favoris.map((f) => {
                    const l = sections.find((s) => s.code === f.code)
                    if (l) return ligneSection(l, true)
                    return (
                      <div key={f.code} className="carte bande-nulle devis-rang" style={{ padding: 0 }}>
                        <div className="devis-ligne">
                          <span className="devis-code">{f.code}</span>
                          <span className="devis-titre">
                            {f.titre ?? 'Sans titre'}
                            <small>Absente du devis importé</small>
                          </span>
                        </div>
                        <button className="devis-etoile oui" disabled={!ecrivable} onClick={() => void suivre(f.code, f.titre, false)} aria-label="Ne plus suivre">
                          ★
                        </button>
                      </div>
                    )
                  })}
                </div>
              </>
            )}

            {devisDocs.length > 0 && (
              <div className="sect">
                <span>
                  Sections du devis <span className="code">{sections.filter((s) => s.code).length}</span>
                </span>
                {admin && sections.length > 0 && (
                  <button className="retour" onClick={() => setEdition((x) => !x)}>
                    {edition ? 'Terminer' : 'Corriger les sections'}
                  </button>
                )}
              </div>
            )}

            {edition ? (
              <EditionSections
                sections={sections}
                devisDocs={devisDocs}
                plages={plages}
                corriger={(p, c, t) => void corriger(p, c, t)}
              />
            ) : (
              <div className="liste" style={{ gap: 4 }}>
                {sections.map((l) => ligneSection(l, true))}
                {devisDocs.length > 0 && sections.length === 0 && (
                  <p className="vide" style={{ padding: '10px 0' }}>Le texte du devis n'a pas encore été lu.</p>
                )}
              </div>
            )}

            {addenda.length > 0 && (
              <>
                <div className="sect">
                  Addenda <span className="code">{addenda.length}</span>
                </div>
                <div className="liste" style={{ gap: 4 }}>
                  {addenda.map((a) => {
                    const vise = a.devis_id ? parId.get(a.devis_id) : null
                    return (
                      <button key={a.id} className="carte bande-nulle devis-ligne" onClick={() => ouvrir(a.id, 1)}>
                        <span className="devis-code">No {a.addenda_numero ?? '?'}</span>
                        <span className="devis-titre">
                          {a.nom}
                          <small>
                            {[dateCourte(a.date_emission), vise ? `modifie ${vise.nom}` : null].filter(Boolean).join(' · ')}
                          </small>
                        </span>
                        <span className="code">{a.nb_pages ? `${a.nb_pages} p.` : ''}</span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            <div className="sect">
              Fichiers <span className="code">{docs.length}</span>
            </div>
            <div className="liste" style={{ gap: 4 }}>
              {[...devisDocs, ...addenda].map((d) => (
                <div key={d.id} className="carte bande-nulle" style={{ padding: '9px 12px' }}>
                  <div className="entre">
                    <p className="titre" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{nomDocument(d)}</p>
                    {!pret(d) && <span className="pastille p-alerte">À préparer</span>}
                  </div>
                  <p className="meta">
                    <span>{d.type === 'devis' ? 'Devis' : 'Addenda'}</span>
                    <span className="pt" />
                    <span>{poids(d.taille_octets)}</span>
                    {d.nb_pages ? (
                      <>
                        <span className="pt" />
                        <span>{d.nb_pages} pages</span>
                      </>
                    ) : null}
                    {d.importe_le && (
                      <>
                        <span className="pt" />
                        <span>importé le {dateCourte(d.importe_le)}</span>
                      </>
                    )}
                  </p>
                  {admin && !travail && (
                    <div className="devis-actions">
                      {!pret(d) && (
                        <button className="retour" style={{ textDecoration: 'underline' }} onClick={() => void reprendre(d)}>
                          Reprendre la préparation
                        </button>
                      )}
                      <button className="retour" style={{ textDecoration: 'underline' }} onClick={() => void supprimer(d)}>
                        Mettre à la corbeille
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )
      )}

      {admin && !travail && !recherche && docs !== null && (
        <>
          {formAddenda ? (
            <FormulaireAddenda
              devisDocs={devisDocs}
              suivant={addenda.length + 1}
              annuler={() => setFormAddenda(false)}
              envoyer={(f, infos) => void importer([f], infos)}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
              <label className="bouton creux" style={{ cursor: 'pointer' }}>
                Importer le devis (un ou plusieurs PDF)
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  hidden
                  onChange={(e) => {
                    const f = [...(e.target.files ?? [])]
                    e.target.value = ''
                    void importer(f, { type: 'devis' })
                  }}
                />
              </label>
              {devisDocs.length > 0 && (
                <button className="bouton creux" onClick={() => setFormAddenda(true)}>
                  Ajouter un addenda
                </button>
              )}
            </div>
          )}
        </>
      )}

      {admin && !recherche && (
        <Corbeille
          chantierId={chantier.id}
          types={['devis', 'addenda']}
          version={versionCorbeille}
          changement={() => void charger()}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------

function Resultats({
  recherche,
  parId,
  ouvrir,
  effacer
}: {
  recherche: { requete: string; termes: string[]; resultats: ResultatDevis[]; plus: boolean }
  parId: Map<string, DocumentDevis>
  ouvrir: (documentId: string, page: number) => void
  effacer: () => void
}) {
  const { resultats } = recherche
  // Devis d'abord, puis les addenda par numéro.
  const groupes = useMemo(() => {
    const m = new Map<string, ResultatDevis[]>()
    for (const r of resultats) m.set(r.document_id, [...(m.get(r.document_id) ?? []), r])
    return [...m.entries()].sort(([a], [b]) => {
      const da = parId.get(a)
      const db = parId.get(b)
      if (!da || !db) return 0
      if (da.type !== db.type) return da.type === 'devis' ? -1 : 1
      return da.type === 'addenda' ? ordreAddenda(da, db) : da.nom.localeCompare(db.nom)
    })
  }, [resultats, parId])

  return (
    <>
      <div className="sect">
        <span>
          {resultats.length === 0
            ? 'Aucune page trouvée'
            : `${recherche.plus ? 'Plus de ' : ''}${resultats.length} page${resultats.length > 1 ? 's' : ''}`}{' '}
          pour « {recherche.requete} »
        </span>
        <button className="retour" onClick={effacer}>
          Effacer
        </button>
      </div>
      {recherche.plus && (
        <p className="avis" style={{ marginBottom: 10 }}>
          Seules les {resultats.length} premières pages sont affichées. Ajoute un mot pour préciser.
        </p>
      )}
      {resultats.length === 0 && (
        <p className="vide" style={{ padding: '10px 0' }}>
          Tous les mots doivent être sur la même page. Mets une expression entre guillemets pour la chercher telle quelle :
          « "isolant giclé" ».
        </p>
      )}
      {groupes.map(([id, liste]) => {
        const d = parId.get(id)
        return (
          <div key={id}>
            {groupes.length > 1 && d && (
              <p className="code" style={{ margin: '12px 0 6px', fontWeight: 700 }}>
                {nomDocument(d)} · {liste.length} page{liste.length > 1 ? 's' : ''}
              </p>
            )}
            <div className="liste" style={{ gap: 6 }}>
              {liste.map((r) => (
                <button
                  key={`${r.document_id}-${r.page}`}
                  className="carte bande-nulle"
                  style={{ textAlign: 'left', width: '100%', padding: '9px 12px' }}
                  onClick={() => ouvrir(r.document_id, r.page)}
                >
                  <div className="entre">
                    <span className="titre">
                      {r.section_code ? <span style={{ fontFamily: 'var(--mono)' }}>{r.section_code} </span> : null}
                      {r.section_titre ?? (r.section_code ? '' : 'Sans section')}
                    </span>
                    <span className="code" style={{ whiteSpace: 'nowrap' }}>
                      p. {r.page}
                      {r.nb > 1 ? ` · ${r.nb}×` : ''}
                    </span>
                  </div>
                  {r.extraits.map((ex, i) => (
                    <p key={i} className="devis-extrait">
                      {ex.map((m, j) => (m.m ? <mark key={j}>{m.t}</mark> : <span key={j}>{m.t}</span>))}
                    </p>
                  ))}
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------

function FormulaireAddenda({
  devisDocs,
  suivant,
  annuler,
  envoyer
}: {
  devisDocs: DocumentDevis[]
  suivant: number
  annuler: () => void
  envoyer: (f: File, infos: InfosImport) => void
}) {
  const [numero, setNumero] = useState(String(suivant))
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA'))
  const [devisId, setDevisId] = useState(devisDocs.length === 1 ? devisDocs[0].id : '')
  const [fichier, setFichier] = useState<File | null>(null)

  return (
    <div className="devis-form">
      <p className="sect" style={{ marginTop: 0 }}>Nouvel addenda</p>
      <label className="champ">
        <span>Numéro</span>
        <input value={numero} onChange={(e) => setNumero(e.target.value)} />
      </label>
      <label className="champ">
        <span>Date d'émission</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      {devisDocs.length > 1 && (
        <label className="champ">
          <span>Devis modifié (facultatif)</span>
          <select value={devisId} onChange={(e) => setDevisId(e.target.value)}>
            <option value="">— Plusieurs ou non précisé —</option>
            {devisDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nom}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="champ">
        <span>Fichier PDF</span>
        <input type="file" accept="application/pdf" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="bouton creux" onClick={annuler}>
          Annuler
        </button>
        <button
          className="bouton"
          disabled={!fichier || !numero.trim()}
          onClick={() =>
            fichier &&
            envoyer(fichier, {
              type: 'addenda',
              addenda_numero: numero.trim(),
              date_emission: date || null,
              devis_id: devisId || null
            })
          }
        >
          Importer
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------

function EditionSections({
  sections,
  devisDocs,
  plages,
  corriger
}: {
  sections: LigneSection[]
  devisDocs: DocumentDevis[]
  plages: PlageSection[]
  corriger: (p: PlageSection[], code: string | null, titre: string | null) => void
}) {
  const [docId, setDocId] = useState(devisDocs[0]?.id ?? '')
  const [page, setPage] = useState('')
  const [code, setCode] = useState('')
  const [titre, setTitre] = useState('')

  function couper() {
    const n = Number(page)
    const p = plages.find((x) => x.document_id === docId && x.debut <= n && x.fin >= n)
    if (!p || !code.trim()) return
    corriger([{ ...p, debut: n }], formaterCode(code), titre.trim() || null)
    setPage('')
    setCode('')
    setTitre('')
  }

  return (
    <>
      <p className="avis" style={{ marginBottom: 10 }}>
        Le code et le titre s'appliquent à toutes les pages de la section. Une section mal découpée ? Donne la page où
        commence la vraie section : elle s'applique de cette page jusqu'à la fin de la section actuelle. Une nouvelle
        lecture du devis ne touche jamais à ces corrections.
      </p>
      <div className="carte bande-nulle" style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {devisDocs.length > 1 && (
          <select value={docId} onChange={(e) => setDocId(e.target.value)} style={{ maxWidth: '100%', padding: 6 }}>
            {devisDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nom}
              </option>
            ))}
          </select>
        )}
        <div className="devis-edition" style={{ padding: 0, flexWrap: 'wrap' }}>
          <input value={page} onChange={(e) => setPage(e.target.value.replace(/\D/g, ''))} placeholder="Page" inputMode="numeric" style={{ width: 64 }} />
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="07 21 00" style={{ width: 92, fontFamily: 'var(--mono)' }} />
          <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre" style={{ flex: 1, minWidth: 120 }} />
          <button className="retour" style={{ textDecoration: 'underline' }} onClick={couper}>
            Appliquer
          </button>
        </div>
      </div>
      <div className="liste" style={{ gap: 4 }}>
        {sections.map((l) => (
          <LigneEditionSection key={l.cle} l={l} corriger={corriger} />
        ))}
      </div>
    </>
  )
}

function LigneEditionSection({
  l,
  corriger
}: {
  l: LigneSection
  corriger: (p: PlageSection[], code: string | null, titre: string | null) => void
}) {
  const [code, setCode] = useState(l.code ?? '')
  const [titre, setTitre] = useState(l.titre ?? '')
  const modifie = code !== (l.code ?? '') || titre !== (l.titre ?? '')
  const sauver = () => modifie && corriger(l.plages, formaterCode(code), titre.trim() || null)
  return (
    <div className="carte bande-nulle devis-edition">
      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onBlur={sauver}
        placeholder="Code"
        style={{ width: 92, fontFamily: 'var(--mono)' }}
      />
      <input
        value={titre}
        onChange={(e) => setTitre(e.target.value)}
        onBlur={sauver}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        placeholder="Titre de la section"
        style={{ flex: 1, minWidth: 0 }}
      />
      <span className="code" style={{ whiteSpace: 'nowrap' }}>p. {l.debut}</span>
    </div>
  )
}

// ---------------------------------------------------------------------

/** Une page du devis ou d'un addenda, dans le visualiseur des plans (sans annotations). */
function LecteurDevis({
  doc,
  pageInitiale,
  pages,
  utilisateurId,
  surligner,
  fermer
}: {
  doc: DocumentDevis
  pageInitiale: number
  pages: PageSectionDevis[]
  utilisateurId: string | null
  surligner?: string[]
  fermer: () => void
}) {
  const [page, setPage] = useState(pageInitiale)
  useHistorique(page, setPage)
  const [complet, setComplet] = useState<PDFDocumentProxy | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const decoupe = Boolean(doc.pages_decoupees && doc.nb_pages)
  const memoire = useRef(new Map<number, Promise<PDFDocumentProxy>>())

  // Document pas encore découpé : on charge le PDF complet.
  useEffect(() => {
    if (decoupe) return
    let actif = true
    let p: PDFDocumentProxy | null = null
    ouvrirPlan(doc)
      .then((x) => {
        p = x
        if (actif) setComplet(x)
        else void x.destroy()
      })
      .catch((e) => actif && setErreur(e instanceof Error ? e.message : String(e)))
    return () => {
      actif = false
      void p?.destroy()
    }
  }, [doc, decoupe])

  useEffect(() => {
    const m = memoire.current
    return () => {
      for (const d of m.values()) void d.then((x) => x.destroy()).catch(() => {})
      m.clear()
    }
  }, [])

  const source = useMemo<SourcePages | null>(() => {
    if (decoupe && doc.nb_pages) {
      return {
        total: doc.nb_pages,
        page: async (n) => {
          const m = memoire.current
          let p = m.get(n)
          if (!p) {
            p = ouvrirPage(doc, n)
            p.catch(() => m.delete(n))
            m.set(n, p)
            while (m.size > 6) {
              const [plusVieille] = m.keys()
              void m.get(plusVieille)?.then((d) => d.destroy()).catch(() => {})
              m.delete(plusVieille)
            }
          }
          return (await p).getPage(1)
        }
      }
    }
    return complet ? { total: complet.numPages, page: (n) => complet.getPage(n) } : null
  }, [decoupe, doc, complet])

  // La section de chaque page s'affiche dans l'en-tête du visualiseur.
  const feuillets = useMemo(() => {
    const m = new Map<number, Feuillet>()
    for (const p of pages) {
      if (p.document_id !== doc.id || !p.section_code) continue
      m.set(p.page, { document_id: doc.id, page: p.page, code: p.section_code, titre: p.section_titre, source: p.section_source })
    }
    return m
  }, [pages, doc.id])

  if (!source) {
    return (
      <Superpose>
        <header className="entete">
          <div className="entete-rang">
            <button className="retour" onClick={fermer}>
              ‹ Devis
            </button>
            <h1 style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nomDocument(doc)}
            </h1>
          </div>
        </header>
        <div className="corps">
          {erreur ? <p className="avis erreur">{erreur}</p> : <p className="vide">Ouverture…</p>}
        </div>
      </Superpose>
    )
  }

  return (
    <VisualiseurPdf
      source={source}
      nomDocument={nomDocument(doc)}
      feuillets={feuillets}
      page={page}
      allerA={setPage}
      index={fermer}
      documentId={doc.id}
      numeroRevision={null}
      peutAnnoter={false}
      utilisateurId={utilisateurId}
      admin={false}
      surligner={surligner}
    />
  )
}
